import { Box, Button, Paper, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { nextStep, ONBOARDING_BASE_PATH } from "../steps/registry";
import { useFlowState } from "../state/FlowProvider";
import { onboardingTokens } from "../theme";
import { t } from "../../../locales/t";

/**
 * Stands in for a step whose screen belongs to a later ticket.
 *
 * The point is that the shell is walkable end to end from day one. Routing,
 * the guards, back-navigation and the progress denominator are all exercisable
 * — by a test and by a person — before a single real screen exists, so those
 * mechanics are proven before feature work starts landing on top of them.
 *
 * Replacing one is a one-line edit to the `SCREENS` map in `routes.jsx`.
 *
 * @param {object} props
 * @param {import('../steps/registry').StepDefinition} props.step
 */
const StepPlaceholder = ({ step }) => {
  const state = useFlowState();
  const navigate = useNavigate();

  const next = nextStep(step.id, state);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        borderRadius: onboardingTokens.radius,
        backgroundColor: onboardingTokens.card,
      }}
      data-testid={`step-placeholder-${step.id}`}
    >
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t("onboarding.placeholder.heading", { step: step.id })}
      </Typography>

      <Typography sx={{ mb: 2, color: onboardingTokens.mutedForeground }}>
        {t("onboarding.placeholder.body")}
      </Typography>

      {step.ticket && (
        <Typography
          variant="caption"
          sx={{ color: onboardingTokens.mutedForeground }}
        >
          {t("onboarding.placeholder.ticket", { ticket: step.ticket })}
        </Typography>
      )}

      {/* Forward only. Back is the layout's, and every screen gets it — two
          controls doing the same thing is one to keep in sync and one for a
          screen reader to announce twice. */}
      <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
        <Button
          variant="contained"
          disabled={!next}
          onClick={() =>
            next && navigate(`${ONBOARDING_BASE_PATH}/${next.path}`)
          }
        >
          {t("onboarding.placeholder.next")}
        </Button>
      </Box>
    </Paper>
  );
};

export default StepPlaceholder;
